import React, { useState } from 'react';
import './ServicePage.css';

interface ServiceItem {
  id: string;
  title: string;
  image: string;
  location: string;
  deskType: 'hot-desk' | 'cabin-desk';
  timeSlot: string;
  basePrice: number;
  gst: number;
  totalPrice: number;
  billingType: 'daily' | 'monthly';
}

interface ServicePageProps {
  onBack: () => void;
  onBookItem?: (item: ServiceItem) => void;
}

export const ServicePage: React.FC<ServicePageProps> = ({ onBack, onBookItem }) => {
  const [activeTab, setActiveTab] = useState('daily');
  const [activeDeskType, setActiveDeskType] = useState('hot-desk');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [selectedTimeSlots, setSelectedTimeSlots] = useState<{[key: string]: string}>({
    'daily-hot': 'morning',
    'daily-cabin': 'morning',
    'monthly-hot': 'morning',
    'monthly-cabin': 'morning'
  });

  const timeSlotPricing = {
    daily: {
      'hot-desk': {
        morning: { timeSlot: '9:30 AM to 5:30 PM', basePrice: 1200 },
        evening: { timeSlot: '3:00 PM to 11:00 PM', basePrice: 1500 },
        night: { timeSlot: '9:00 PM to 5:00 AM', basePrice: 1800 }
      },
      'cabin-desk': {
        morning: { timeSlot: '9:30 AM to 5:30 PM', basePrice: 1500 },
        evening: { timeSlot: '3:00 PM to 11:00 PM', basePrice: 1800 },
        night: { timeSlot: '9:00 PM to 5:00 AM', basePrice: 2000 }
      }
    },
    monthly: {
      'hot-desk': {
        morning: { timeSlot: '9:30 AM to 5:30 PM', basePrice: 7000 },
        evening: { timeSlot: '3:00 PM to 11:00 PM', basePrice: 8000 },
        night: { timeSlot: '9:00 PM to 5:00 AM', basePrice: 9000 }
      },
      'cabin-desk': {
        morning: { timeSlot: '9:30 AM to 5:30 PM', basePrice: 8000 },
        evening: { timeSlot: '3:00 PM to 11:00 PM', basePrice: 9000 },
        night: { timeSlot: '9:00 PM to 5:00 AM', basePrice: 10000 }
      }
    }
  };

  const workspaceLocations = [
    {
      id: 1,
      name: 'CoSpace BKC',
      location: 'Bandra Kurla Complex, Mumbai',
      image: 'https://images.unsplash.com/photo-1497366216548-37526070297c?w=300&h=200&fit=crop',
      cabinImage: 'https://images.unsplash.com/photo-1497366754035-f200968a6e72?w=300&h=200&fit=crop'
    },
    {
      id: 2,
      name: 'CoSpace Andheri',
      location: 'Andheri East, Mumbai',
      image: 'https://images.unsplash.com/photo-1524758631624-e2822e304c36?w=300&h=200&fit=crop',
      cabinImage: 'https://images.unsplash.com/photo-1556761175-b413da4baf72?w=300&h=200&fit=crop'
    },
    {
      id: 3,
      name: 'CoSpace Lower Parel',
      location: 'Lower Parel, Mumbai',
      image: 'https://images.unsplash.com/photo-1497366811353-6870744d04b2?w=300&h=200&fit=crop',
      cabinImage: 'https://images.unsplash.com/photo-1560472354-b33ff0c44a43?w=300&h=200&fit=crop'
    },
    {
      id: 4,
      name: 'CoSpace Powai',
      location: 'Powai, Mumbai',
      image: 'https://images.unsplash.com/photo-1571624436279-b272aff752b5?w=300&h=200&fit=crop',
      cabinImage: 'https://images.unsplash.com/photo-1568992687947-868a62a9f521?w=300&h=200&fit=crop'
    },
    {
      id: 5,
      name: 'CoSpace Malad',
      location: 'Malad West, Mumbai',
      image: 'https://images.unsplash.com/photo-1586023492125-27b2c045efd7?w=300&h=200&fit=crop',
      cabinImage: 'https://images.unsplash.com/photo-1600298881974-6be191ceeda1?w=300&h=200&fit=crop'
    },
    {
      id: 6,
      name: 'CoSpace Thane',
      location: 'Thane West, Mumbai',
      image: 'https://images.unsplash.com/photo-1600298882974-db8ba2d0c6c4?w=300&h=200&fit=crop',
      cabinImage: 'https://images.unsplash.com/photo-1600298882974-db8ba2d0c6c4?w=300&h=200&fit=crop'
    }
  ];

  const getCurrentItems = () => {
    return workspaceLocations.map(workspace => {
      const slotKey = `${activeTab}-${activeDeskType.replace('-desk', '')}`;
      const selectedSlot = selectedTimeSlots[slotKey];
      const pricing = timeSlotPricing[activeTab as 'daily' | 'monthly'][activeDeskType as 'hot-desk' | 'cabin-desk'][selectedSlot as 'morning' | 'evening' | 'night'];
      
      return {
        id: `${activeTab}-${activeDeskType}-${selectedSlot}-${workspace.id}`,
        title: `${activeDeskType === 'hot-desk' ? 'Hot Desk' : 'Private Cabin Desk'} - ${workspace.name}`,
        image: activeDeskType === 'hot-desk' ? workspace.image : workspace.cabinImage,
        location: workspace.location,
        deskType: activeDeskType as 'hot-desk' | 'cabin-desk',
        timeSlot: pricing.timeSlot,
        basePrice: pricing.basePrice,
        gst: Math.round(pricing.basePrice * 0.18),
        totalPrice: Math.round(pricing.basePrice * 1.18),
        billingType: activeTab as 'daily' | 'monthly'
      };
    });
  };

  const handleTimeSlotChange = (timeSlot: string) => {
    const slotKey = `${activeTab}-${activeDeskType.replace('-desk', '')}`;
    setSelectedTimeSlots(prev => ({ ...prev, [slotKey]: timeSlot }));
  };

  const filteredItems = getCurrentItems();

  return (
    <div className="service-page">
      <div className="two-column-layout">
        {/* Left Column - Filters */}
        <div className="left-column">
          <div className="toggle-tabs">
            <button 
              className={activeTab === 'daily' ? 'active' : ''}
              onClick={() => setActiveTab('daily')}
            >
              Daily Booking
            </button>
            <button 
              className={activeTab === 'monthly' ? 'active' : ''}
              onClick={() => setActiveTab('monthly')}
            >
              Monthly Booking
            </button>
          </div>

          <div className="desk-type-tabs">
            <button 
              className={activeDeskType === 'hot-desk' ? 'active' : ''}
              onClick={() => setActiveDeskType('hot-desk')}
            >
              Hot Desks
            </button>
            <button 
              className={activeDeskType === 'cabin-desk' ? 'active' : ''}
              onClick={() => setActiveDeskType('cabin-desk')}
            >
              Cabin Desks
            </button>
          </div>



          <div className="filter-section">
            <div className="date-filters">
              <div className="date-input">
                <label className="date-label">Start Date</label>
                <input 
                  type="date" 
                  value={startDate} 
                  onChange={(e) => setStartDate(e.target.value)}
                  placeholder="Start Date"
                />
              </div>
              <div className="date-input">
                <label className="date-label">End Date</label>
                <input 
                  type="date" 
                  value={endDate} 
                  onChange={(e) => setEndDate(e.target.value)}
                  placeholder="End Date"
                />
              </div>
            </div>
            
            <button className="apply-filters"><span>Search</span></button>
          </div>
        </div>

        {/* Right Column - Results */}
        <div className="right-column">
          <div className="section-header">
            <h3>{activeTab === 'daily' ? 'Daily' : 'Monthly'} {activeDeskType === 'hot-desk' ? 'Hot Desks' : 'Private Cabins'}</h3>
            <span>Results: {filteredItems.length} locations</span>
          </div>

          {/* Time Slot Selector - Global */}
          <div className="global-time-selector">
            <label className="selector-label">Select Time Slot for All Locations:</label>
            <div className="time-options-horizontal">
              <label className="time-option">
                <input 
                  type="radio" 
                  name={`globalTimeSlot-${activeDeskType}`}
                  value="morning"
                  checked={selectedTimeSlots[`${activeTab}-${activeDeskType.replace('-desk', '')}`] === 'morning'}
                  onChange={() => handleTimeSlotChange('morning')}
                />
                <span>Morning (9:30 AM - 5:30 PM)</span>
              </label>
              <label className="time-option">
                <input 
                  type="radio" 
                  name={`globalTimeSlot-${activeDeskType}`}
                  value="evening"
                  checked={selectedTimeSlots[`${activeTab}-${activeDeskType.replace('-desk', '')}`] === 'evening'}
                  onChange={() => handleTimeSlotChange('evening')}
                />
                <span>Evening (3:00 PM - 11:00 PM)</span>
              </label>
              <label className="time-option">
                <input 
                  type="radio" 
                  name={`globalTimeSlot-${activeDeskType}`}
                  value="night"
                  checked={selectedTimeSlots[`${activeTab}-${activeDeskType.replace('-desk', '')}`] === 'night'}
                  onChange={() => handleTimeSlotChange('night')}
                />
                <span>Night (9:00 PM - 5:00 AM)</span>
              </label>
            </div>
          </div>

          <div className="cards-grid">
            {filteredItems.map((item) => (
              <div key={item.id} className="result-card">
                <img src={item.image} alt={item.title} className="card-image" />
                <div className="card-content">
                  <div className="card-header">
                    <h4>{item.title}</h4>
                  </div>
                  <p className="location">📍 {item.location}</p>
                  <div className="pricing-details">
                    <div className="price-display">₹{item.basePrice.toLocaleString()}/{item.billingType === 'daily' ? 'day' : 'month'}</div>
                  </div>
                  <div className="card-footer">
                    <button 
                      className="book-btn" 
                      onClick={() => onBookItem?.(item)}
                    >
                      <span>Book Now</span>
                    </button>
                  </div>
                  {item.deskType === 'cabin-desk' && (
                    <div className="cabin-features">
                      <span>🔒 Lockable cabin</span>
                      <span>🗄️ Lockable drawers</span>
                      <span>🌐 Ethernet connection</span>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};